import type { ReactNode } from "react";
import { FilmSlate } from "@phosphor-icons/react";
import { useMockQuery } from "@/lib/api/mock-query";
import { LoadingState } from "./LoadingState";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { errorCopy } from "./errorCopy";

export interface PageStateGateQuery {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: unknown;
  refetch: () => void;
}

export interface PageStateGateProps {
  /** PT-BR copy for the empty state. */
  empty: {
    icon?: ReactNode;
    title: string;
    description?: string;
    action?: ReactNode;
  };
  /** Accessible label for the skeleton. Default: "Carregando". */
  loadingLabel?: string;
  /** Number of skeleton rows. Default: 5. */
  rows?: number;
  /** Success-branch children (the page body). */
  children: ReactNode;
  /**
   * When provided, drives state from a real React Query result instead of
   * the dev-only `useMockQuery`. The Story 2.1 `?state=` URL helper is still
   * honored when `query` is omitted.
   *
   * Spec: docs/architecture/data-layer-slicing-plan.md §6
   */
  query?: PageStateGateQuery;
}

function isQueryEmpty(data: unknown): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

/**
 * Page-level state gate. Wraps the page body and renders the canonical
 * `<LoadingState>` / `<ErrorState>` / `<EmptyState>` primitives based on
 * either the `query` prop (real data) or the `?state=` URL param fallback
 * (Story 2.1 dev helper).
 *
 * Spec: docs/architecture/states-pattern.md §1 / §8.
 */
export function PageStateGate({
  empty,
  loadingLabel = "Carregando",
  rows = 5,
  children,
  query,
}: PageStateGateProps): ReactNode {
  const mockQuery = useMockQuery<true>(true);

  // When the caller supplies a real query, the URL `?state=` helper still
  // wins (so dev tooling keeps working), but only for pending/error/empty
  // forced states. Otherwise the real query result is the source of truth.
  if (mockQuery.forcedState === "loading") {
    return <LoadingState variant="skeleton" rows={rows} label={loadingLabel} />;
  }
  if (mockQuery.forcedState === "error") {
    return (
      <ErrorState
        message={errorCopy(mockQuery.error)}
        retry={mockQuery.refetch}
        variant="page"
      />
    );
  }
  if (mockQuery.forcedState === "empty") {
    return (
      <EmptyState
        icon={empty.icon ?? <FilmSlate weight="duotone" className="h-6 w-6" aria-hidden="true" />}
        title={empty.title}
        description={empty.description}
        action={empty.action}
      />
    );
  }

  if (query) {
    if (query.isPending) {
      return <LoadingState variant="skeleton" rows={rows} label={loadingLabel} />;
    }
    if (query.isError) {
      return (
        <ErrorState
          message={errorCopy(query.error)}
          retry={query.refetch}
          variant="page"
        />
      );
    }
    if (isQueryEmpty(query.data)) {
      return (
        <EmptyState
          icon={empty.icon ?? <FilmSlate weight="duotone" className="h-6 w-6" aria-hidden="true" />}
          title={empty.title}
          description={empty.description}
          action={empty.action}
        />
      );
    }
  }

  return <>{children}</>;
}
